import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Grid, Stack, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import { GlassCard } from '../../../../../components/glass';
import type { IContaCilMesStatus } from '../../../../../hooks/useContaCilDashboard';

const MESES_ABBR = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface CelStatus {
  cor: string;
  label: string;
  futuro: boolean;
}

function statusCel(m: IContaCilMesStatus, futuro: boolean): CelStatus {
  if (futuro) return { cor: '#CBD5E1', label: 'futuro', futuro: true };
  if (m.total === 0) return { cor: '#94A3B8', label: 'sem movimento', futuro: false };
  const pct = (m.conciliados / m.total) * 100;
  if (pct >= 85) return { cor: '#10B981', label: 'concluído', futuro: false };
  return { cor: '#D9A441', label: 'parcial', futuro: false };
}

interface VolumeCalendarProps {
  meses: IContaCilMesStatus[];
  exportadosSerie: number[]; // exportados por mês (12)
  ano: number;
}

export const VolumeCalendar = ({ meses, exportadosSerie, ano }: VolumeCalendarProps) => {
  const hoje = new Date();
  const isFuturo = (mes: number) =>
    ano > hoje.getFullYear() || (ano === hoje.getFullYear() && mes > hoje.getMonth() + 1);

  // Mês selecionado: default = último com movimento.
  const ultimoAtivo = useMemo(() => {
    const ativos = meses.filter(m => m.total > 0 && !isFuturo(m.mes));
    return ativos.length ? ativos[ativos.length - 1].mes : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meses, ano]);
  const [sel, setSel] = useState<number | null>(null);
  // Troca de ano → limpa a seleção (senão o painel escuro mostra o mês do ano antigo).
  useEffect(() => {
    setSel(null);
  }, [ano]);
  const mesSel = sel ?? ultimoAtivo;
  const detalhe = meses.find(m => m.mes === mesSel) ?? null;
  const expSel = mesSel ? (exportadosSerie[mesSel - 1] ?? 0) : 0;

  return (
    <Grid templateColumns={{ base: '1fr', lg: '1fr 300px' }} gap={5}>
      {/* Calendário */}
      <GlassCard intensity="medium" p={6}>
        <Text
          fontSize="10px"
          fontWeight="600"
          color="#94A3B8"
          textTransform="uppercase"
          letterSpacing="0.08em"
          mb={1}
        >
          Volume mensal · {ano}
        </Text>
        <Text fontSize="15px" fontWeight="700" color="#0F172A" mb={4} letterSpacing="-0.01em">
          Extratos e conciliação por mês
        </Text>
        <Grid templateColumns={{ base: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }} gap={2.5}>
          {meses.map(m => {
            const fut = isFuturo(m.mes);
            const st = statusCel(m, fut);
            const pct = m.total > 0 ? (m.conciliados / m.total) * 100 : 0;
            const ativo = m.mes === mesSel;
            return (
              <Box
                as="button"
                key={m.mes}
                onClick={() => !fut && setSel(m.mes)}
                cursor={fut ? 'default' : 'pointer'}
                opacity={fut ? 0.5 : 1}
                p={2.5}
                borderRadius="13px"
                bg={ativo ? 'rgba(124,58,237,0.06)' : 'rgba(15,23,42,0.02)'}
                borderWidth="1.5px"
                borderColor={ativo ? '#7C3AED' : 'transparent'}
                textAlign="left"
                transition="all 0.15s"
                _hover={fut ? {} : { bg: 'rgba(124,58,237,0.05)' }}
              >
                <Flex align="center" justify="space-between" mb={1}>
                  <Text fontSize="10px" fontWeight="700" color="#64748B" textTransform="uppercase">
                    {MESES_ABBR[m.mes - 1]}
                  </Text>
                  <Box w="7px" h="7px" borderRadius="full" bg={st.cor} />
                </Flex>
                <Text
                  fontSize="20px"
                  fontWeight="700"
                  color="#0F172A"
                  lineHeight="1"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fut ? '·' : formatNumber(m.total)}
                </Text>
                <Text fontSize="9.5px" color="#94A3B8" mb={1.5}>
                  {fut ? '—' : `${formatNumber(m.empresas)} empresas`}
                </Text>
                <Box h="4px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                  <Box h="full" w={`${pct}%`} bg="#10B981" borderRadius="full" />
                </Box>
              </Box>
            );
          })}
        </Grid>
        <Flex gap={4} mt={4} wrap="wrap">
          {[
            { c: '#94A3B8', t: 'Sem movimento' },
            { c: '#D9A441', t: 'Parcial' },
            { c: '#10B981', t: 'Concluído' },
          ].map(l => (
            <Flex key={l.t} align="center" gap={1.5}>
              <Box w="8px" h="8px" borderRadius="full" bg={l.c} />
              <Text fontSize="10px" color="#64748B">
                {l.t}
              </Text>
            </Flex>
          ))}
        </Flex>
      </GlassCard>

      {/* Painel de detalhe (escuro) */}
      <Box
        borderRadius="18px"
        p={6}
        bg="linear-gradient(160deg, #1c2036, #171a2e)"
        boxShadow="0 12px 30px -18px rgba(18,20,40,0.5)"
      >
        {detalhe && mesSel ? (
          <motion.div
            key={mesSel}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Text
              fontSize="10px"
              fontWeight="600"
              color="#8f95bf"
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={1}
            >
              Detalhe do mês
            </Text>
            <Text fontSize="22px" fontWeight="700" color="#e8eaf6" mb={5} letterSpacing="-0.01em">
              {MESES_FULL[mesSel - 1]}
            </Text>

            <Stack spacing={3.5}>
              {[
                { label: 'Lançamentos', valor: detalhe.total },
                { label: 'Empresas em operação', valor: detalhe.empresas },
                { label: 'Exportados p/ Domínio', valor: expSel },
              ].map(r => (
                <Flex key={r.label} justify="space-between" align="baseline">
                  <Text fontSize="12px" color="#a8adcf">
                    {r.label}
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="700"
                    color="#e8eaf6"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(r.valor)}
                  </Text>
                </Flex>
              ))}
            </Stack>

            <Box mt={5} pt={4} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
              <Text
                fontSize="10px"
                color="#8f95bf"
                textTransform="uppercase"
                letterSpacing="0.06em"
                mb={2}
              >
                Lançamentos
              </Text>
              <Flex h="10px" borderRadius="full" overflow="hidden" bg="rgba(255,255,255,0.06)">
                <Box
                  bg="#4ade9b"
                  w={`${detalhe.total > 0 ? (detalhe.conciliados / detalhe.total) * 100 : 0}%`}
                />
                <Box bg="#e0932f" flex={1} />
              </Flex>
              <Flex justify="space-between" mt={2}>
                <Text fontSize="11px" color="#4ade9b" fontWeight="600">
                  {formatNumber(detalhe.conciliados)} conciliados
                </Text>
                <Text fontSize="11px" color="#e0932f" fontWeight="600">
                  {formatNumber(Math.max(0, detalhe.total - detalhe.conciliados))} não
                </Text>
              </Flex>
            </Box>
          </motion.div>
        ) : (
          <Flex align="center" justify="center" h="full" minH="220px">
            <Text fontSize="13px" color="#8f95bf" textAlign="center">
              Toque num mês do calendário para ver o detalhe.
            </Text>
          </Flex>
        )}
      </Box>
    </Grid>
  );
};
